import { useEffect, RefObject } from 'react';
import { View } from 'react-native';
import {
  DragOptions,
  ScrollableListComponent,
} from './useHorizontalDrag.types';

export default function useHorizontalDrag<T>(
  containerRef: RefObject<View>,
  listRef: RefObject<ScrollableListComponent<T>>,
  scrollOffsetRef: RefObject<number>,
  options: DragOptions = {},
) {
  useEffect(() => {
    const {
      speedMultiplier = 1.5,
      friction = 0.95,
      momentumMultiplier = 4.5,
      velocityThreshold = 0.05,
      onDragStart,
      onDragEnd,
    } = options;

    if (!containerRef.current) return;

    const containerElement = containerRef.current as unknown as HTMLElement;
    if (!('addEventListener' in containerElement)) return;

    let isMouseDown = false;
    let startX = 0;
    let scrollStartOffset = 0;
    let velocityTracker: { x: number; time: number }[] = [];
    let animationFrameId: number | null = null;
    let velocity = 0;

    const handleMouseDown = (e: MouseEvent) => {
      isMouseDown = true;
      startX = e.clientX;
      scrollStartOffset = scrollOffsetRef.current || 0;
      velocityTracker = [];
      velocityTracker.push({ x: e.clientX, time: Date.now() });

      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      velocity = 0;

      onDragStart?.();

      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isMouseDown) return;

      velocityTracker.push({ x: e.clientX, time: Date.now() });
      if (velocityTracker.length > 5) {
        velocityTracker.shift();
      }

      const dragDistance = startX - e.clientX;

      if (listRef.current) {
        const newOffset = scrollStartOffset + dragDistance * speedMultiplier;
        const finalOffset = Math.max(0, newOffset);

        listRef.current.scrollToOffset({
          offset: finalOffset,
          animated: false,
        });
      }
    };

    const calculateVelocity = () => {
      if (velocityTracker.length < 2) return 0;

      const recent = velocityTracker.slice(-3);
      const first = recent[0];
      const last = recent[recent.length - 1];
      const deltaTime = last.time - first.time;

      if (deltaTime <= 0) return 0;

      const deltaX = first.x - last.x;
      return deltaX / deltaTime;
    };

    const applyMomentum = () => {
      const easedFriction = Math.abs(velocity) > 1 ? friction : friction * 0.98;

      velocity *= easedFriction;

      if (Math.abs(velocity) > velocityThreshold && listRef.current) {
        const momentumDistance = velocity * 16;
        const newOffset = (scrollOffsetRef.current || 0) + momentumDistance;
        const finalOffset = Math.max(0, newOffset);

        listRef.current.scrollToOffset({
          offset: finalOffset,
          animated: false,
        });

        animationFrameId = requestAnimationFrame(applyMomentum);
      } else {
        animationFrameId = null;
      }
    };

    const handleMouseUp = () => {
      if (!isMouseDown) return;
      isMouseDown = false;

      velocity = calculateVelocity() * momentumMultiplier;

      onDragEnd?.();

      if (Math.abs(velocity) > velocityThreshold) {
        setTimeout(() => {
          if (!isMouseDown && animationFrameId === null) {
            animationFrameId = requestAnimationFrame(applyMomentum);
          }
        }, 10);
      }
    };

    containerElement.addEventListener('mousedown', handleMouseDown);
    containerElement.addEventListener('mousemove', handleMouseMove);
    containerElement.addEventListener('mouseup', handleMouseUp);
    containerElement.addEventListener('mouseleave', handleMouseUp);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      containerElement.removeEventListener('mousedown', handleMouseDown);
      containerElement.removeEventListener('mousemove', handleMouseMove);
      containerElement.removeEventListener('mouseup', handleMouseUp);
      containerElement.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [containerRef, listRef, scrollOffsetRef, options]);
}
